import { IgButton, IgLink, IgTypography } from "@intelligems/ig-theme";
import { IgAppBanner } from "@src/components/Utils/IgAppBanner";
import { useDialogs } from "@src/hooks/dialog";
import { allIntegrationGuides } from "@src/utils/linkSheet";
import { openNewTab } from "@src/utils/openNewTab";

import { RecheckButton } from "./RecheckButton";
import { ExpectedFoundProp, OrgIdProp, ShopIdProp, VariantProp } from "./types";
import { useTheme } from "@mui/material";

export const ScriptMissingAlert = ({
  orgId,
  additionalAlert,
  endContent,
}: OrgIdProp & VariantProp) => {
  const { openDialog } = useDialogs();

  return (
    <IgAppBanner
      dismissId="script-status-alert"
      title="Turn on the Intelligems script"
      message="in your Shopify theme to start testing."
      variant={additionalAlert ? "secondary" : "info"}
      buttons={[
        <RecheckButton
          key="recheck"
          orgId={orgId}
          additionalAlert={additionalAlert}
        />,
        <IgButton
          key="connect"
          size="extraSmall"
          variant="contained"
          color={additionalAlert ? "primary" : "secondary"}
          onClick={() => openDialog("scriptTag")}
        >
          Connect Intelligems
        </IgButton>,
      ]}
      endContent={endContent}
    />
  );
};

export const ScriptOutdatedAlert = ({
  orgId,
  additionalAlert,
  endContent,
}: OrgIdProp & VariantProp) => {
  const { openDialog } = useDialogs();

  return (
    <IgAppBanner
      dismissId="script-status-alert"
      title="We're making things better!"
      message="To enjoy new app improvements, update your Intelligems script in Shopify."
      variant={additionalAlert ? "secondary" : "info"}
      buttons={[
        <RecheckButton
          key="recheck"
          orgId={orgId}
          additionalAlert={additionalAlert}
        />,
        <IgButton
          key="update"
          size="extraSmall"
          variant="contained"
          color={additionalAlert ? "primary" : "secondary"}
          onClick={() => openDialog("scriptTag")}
        >
          Update Intelligems
        </IgButton>,
      ]}
      endContent={endContent}
    />
  );
};

export const ScriptIncompleteAlert = ({
  orgId,
  additionalAlert,
  endContent,
}: OrgIdProp & VariantProp) => {
  const { openDialog } = useDialogs();

  return (
    <IgAppBanner
      dismissId="script-status-alert"
      title="Incomplete Intelligems script detected."
      message="Part of the theme script is missing, so certain features may not work properly."
      variant={additionalAlert ? "secondary" : "info"}
      buttons={[
        <RecheckButton
          key="recheck"
          orgId={orgId}
          additionalAlert={additionalAlert}
        />,
        <IgButton
          key="update"
          size="extraSmall"
          variant="contained"
          color={additionalAlert ? "primary" : "secondary"}
          onClick={() => openDialog("scriptTag")}
        >
          Update Intelligems
        </IgButton>,
      ]}
      endContent={endContent}
    />
  );
};

export const ScriptMultipleWithAppEmbedAlert = ({
  orgId,
  shopId,
  additionalAlert,
  endContent,
}: OrgIdProp & ShopIdProp & VariantProp) => {
  const theme = useTheme();
  return (
    <IgAppBanner
      dismissId="script-status-alert"
      title="Duplicate scripts detected."
      message={
        <IgTypography color={theme.palette.text.dark}>
          Both the App Embed and a manual script tag are detected. Remove the
          script tag from your theme code to continue.{" "}
          <IgLink
            isDarkColorVariant={true}
            sx={{ textDecoration: "underline" }}
            onClick={() =>
              openNewTab(allIntegrationGuides, {
                mixpanelTracker: "All Integration Guides",
              })
            }
          >
            Learn more
          </IgLink>
        </IgTypography>
      }
      variant={additionalAlert ? "secondary" : "info"}
      buttons={[
        <RecheckButton
          key="recheck"
          orgId={orgId}
          additionalAlert={additionalAlert}
        />,
        <IgButton
          key="edit"
          size="extraSmall"
          variant="contained"
          color={additionalAlert ? "primary" : "secondary"}
          onClick={() =>
            openNewTab(
              `${shopId}/admin/themes/current/?key=layout%2Ftheme.liquid`,
              { noUtmParams: true }
            )
          }
        >
          Edit Theme
        </IgButton>,
      ]}
      endContent={endContent}
    />
  );
};

export const ScriptMultipleAlert = ({
  orgId,
  shopId,
  additionalAlert,
  endContent,
}: OrgIdProp & ShopIdProp & VariantProp) => (
  <IgAppBanner
    dismissId="script-status-alert"
    title="Duplicate scripts detected."
    message="Multiple scripts have been detected."
    variant={additionalAlert ? "secondary" : "info"}
    buttons={[
      <RecheckButton
        key="recheck"
        orgId={orgId}
        additionalAlert={additionalAlert}
      />,
      <IgButton
        key="edit"
        size="extraSmall"
        variant="contained"
        color={additionalAlert ? "primary" : "secondary"}
        onClick={() =>
          openNewTab(
            `${shopId}/admin/themes/current/?key=layout%2Ftheme.liquid`,
            { noUtmParams: true }
          )
        }
      >
        Edit Theme
      </IgButton>,
    ]}
    endContent={endContent}
  />
);

export const ScriptIncorrectAlert = ({
  orgId,
  expected,
  found,
  additionalAlert,
  endContent,
}: OrgIdProp & ExpectedFoundProp & VariantProp) => {
  const { openDialog } = useDialogs();

  return (
    <IgAppBanner
      dismissId="script-status-alert"
      title="Incorrect Intelligems script detected."
      message={`Expected script ending in ${expected} but found ${found}.`}
      variant={additionalAlert ? "secondary" : "info"}
      buttons={[
        <RecheckButton
          key="recheck"
          orgId={orgId}
          additionalAlert={additionalAlert}
        />,
        <IgButton
          key="connect"
          size="extraSmall"
          variant="contained"
          color={additionalAlert ? "primary" : "secondary"}
          onClick={() => openDialog("scriptTag")}
        >
          Connect Intelligems
        </IgButton>,
      ]}
      endContent={endContent}
    />
  );
};
